import { useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react'
import { AlertTriangle, Check, CircleAlert, LoaderCircle, Search, X } from 'lucide-react'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import { humanize } from './format'

gsap.registerPlugin(useGSAP)

export function Button({ variant = 'secondary', icon, children, className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'quiet' | 'danger'; icon?: ReactNode }) {
  return <button className={`button button--${variant} ${className}`} {...props}>{icon}{children}</button>
}

export function IconButton({ label, children, className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }) {
  return <button className={`icon-button ${className}`} aria-label={label} title={label} {...props}>{children}</button>
}

export function StatusBadge({ status, detail }: { status?: string | null; detail?: string }) {
  const normalized = (status || 'INFORMACAO').toLocaleLowerCase('pt-BR')
  const tone = /conclu|entregue|apta|ativo|normal|sucesso|valido/.test(normalized) ? 'success'
    : /falha|critico|vencido|cancelado|negado|bloqueado|divergente/.test(normalized) ? 'danger'
      : /pendente|atencao|pausad|ausente|manutencao/.test(normalized) ? 'warning'
        : /andamento|sessao|confirmado|checkin|chegou|preparacao|agendad/.test(normalized) ? 'info' : 'neutral'
  return <span className={`status status--${tone}`}><span className="status__dot" />{humanize(status)}{detail ? <small>{detail}</small> : null}</span>
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description?: string; actions?: ReactNode }) {
  return <header className="page-header">
    <div>
      {eyebrow ? <p className="page-header__eyebrow">{eyebrow}</p> : null}
      <h1>{title}</h1>
      {description ? <p className="page-header__description">{description}</p> : null}
    </div>
    {actions ? <div className="page-header__actions">{actions}</div> : null}
  </header>
}

export function Panel({ title, description, action, children, className = '' }: { title?: string; description?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return <section className={`panel ${className}`}>
    {title || action ? <header className="panel__header"><div>{title ? <h2>{title}</h2> : null}{description ? <p>{description}</p> : null}</div>{action}</header> : null}
    {children}
  </section>
}

export function Field({ label, error, hint, children, required }: { label: string; error?: string; hint?: string; children: ReactNode; required?: boolean }) {
  const id = useId()
  return <label className={`field ${error ? 'field--error' : ''}`} data-field-id={id}>
    <span className="field__label">{label}{required ? <span aria-label="obrigatório"> *</span> : null}</span>
    {children}
    {error ? <span className="field__error"><CircleAlert size={14} />{error}</span> : hint ? <span className="field__hint">{hint}</span> : null}
  </label>
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="input" {...props} />
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className="input select" {...props} />
}

export function SearchInput({ value, onChange, placeholder = 'Buscar' }: { value: string; onChange: (value: string) => void; placeholder?: string }) {
  return <label className="search-input"><Search size={17} aria-hidden="true" /><span className="sr-only">{placeholder}</span><input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} /></label>
}

export function LoadingState({ label = 'Carregando dados' }: { label?: string }) {
  return <div className="state state--loading" role="status"><LoaderCircle className="spin" size={22} /><span>{label}…</span></div>
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return <div className="state state--empty"><Check size={22} /><strong>{title}</strong><p>{description}</p>{action}</div>
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  const recover = 'recuperacao' in error ? String(error.recuperacao || '') : ''
  return <div className="state state--error" role="alert"><AlertTriangle size={22} /><strong>{error.message}</strong>{recover ? <p>{recover}</p> : null}{onRetry ? <Button onClick={onRetry}>Tentar novamente</Button> : null}</div>
}

export function Drawer({ open, title, description, onClose, children, width = 'wide' }: { open: boolean; title: string; description?: string; onClose: () => void; children: ReactNode; width?: 'medium' | 'wide' }) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  useGSAP(() => {
    if (!open || !bodyRef.current || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    gsap.fromTo(bodyRef.current, { x: 28, autoAlpha: 0.65 }, { x: 0, autoAlpha: 1, duration: 0.24, ease: 'power3.out' })
  }, { dependencies: [open], scope: bodyRef, revertOnUpdate: true })

  return <dialog ref={dialogRef} className={`drawer drawer--${width}`} onCancel={(event) => { event.preventDefault(); onClose() }} onClick={(event) => { if (event.target === dialogRef.current) onClose() }}>
    <div className="drawer__body" ref={bodyRef}>
      <header className="drawer__header"><div><h2>{title}</h2>{description ? <p>{description}</p> : null}</div><IconButton label="Fechar painel" onClick={onClose}><X size={20} /></IconButton></header>
      <div className="drawer__content">{children}</div>
    </div>
  </dialog>
}

export function SegmentedControl<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: string; count?: number }[]; onChange: (value: T) => void; label: string }) {
  return <div className="segmented" role="group" aria-label={label}>{options.map((option) => <button type="button" key={option.value} className={value === option.value ? 'is-active' : ''} onClick={() => onChange(option.value)}>{option.label}{option.count !== undefined ? <span>{option.count}</span> : null}</button>)}</div>
}

export function InlineNotice({ tone = 'info', title, children }: { tone?: 'info' | 'warning' | 'danger' | 'success'; title: string; children: ReactNode }) {
  return <div className={`notice notice--${tone}`}><CircleAlert size={18} /><div><strong>{title}</strong><p>{children}</p></div></div>
}

export function TableWrap({ children }: { children: ReactNode }) {
  return <div className="table-wrap">{children}</div>
}

